import React, { useEffect, useState } from 'react';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

export interface IFormControlTextArea {
  placeholder?: string;
  value?: string;
  form?: any;
  name: string;
  className?: string;
  /** Pixel width, CSS string (`100%`), or omit for default 240px. */
  width?: number | string;
  height?: number;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  style?: React.CSSProperties;
  /** Already-translated label text (same contract as FormControlInput). */
  label?: string;
  gap?: string;
  error?: string;
  touched?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  isValidate?: boolean;
  isRead?: boolean;
  rows?: number;
}

function resolveWidth(width: number | string | undefined): string {
  if (width == null) return '240px';
  return typeof width === 'number' ? `${width}px` : width;
}

const FormControlTextArea: React.FC<IFormControlTextArea> = (props) => {
  const { t } = useTranslation();
  const {
    placeholder,
    name,
    onChange,
    onFocus,
    style,
    form,
    className,
    width,
    label,
    gap,
    height,
    value,
    error,
    touched,
    disabled = false,
    isRequired: explicitIsRequired,
    isValidate = false,
    rows = 4,
  } = props;
  const widthCss = resolveWidth(width);
  const _height = height || 96;

  const [localValue, setLocalValue] = useState(value ?? '');

  useEffect(() => {
    if (!form && value !== undefined) {
      setLocalValue(value);
    }
  }, [value, form]);

  const inputValue = form ? (form.values?.[name] ?? '') : localValue;
  const inputError = form ? form.errors?.[name] : error;
  const inputTouched = form ? form.touched?.[name] : touched;
  const isInvalid = !!inputError && !!inputTouched;

  const isRequired =
    explicitIsRequired !== undefined
      ? explicitIsRequired
      : typeof form?.isRequired === 'function'
        ? form.isRequired(name)
        : false;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (form && !onChange) {
      form.setFieldValue(name, newValue);
      return;
    }
    if (!form) setLocalValue(newValue);
    onChange?.(newValue);
  };

  const handleBlur = () => {
    form?.setFieldTouched(name, true);
  };

  return (
    <FormControl
      className={`text-area-container ${className ?? ''}`}
      width={widthCss}
      style={style}
      isRequired={isRequired}
      isInvalid={isInvalid}
    >
      <Flex direction="column" gap={gap}>
        {label ? <FormLabel htmlFor={name}>{label}</FormLabel> : null}
        {props.isRead ? (
          <Text>{inputValue || '-'}</Text>
        ) : (
          <Textarea
            id={name}
            rows={rows}
            overflow="auto"
            width="100%"
            height={`${_height}px`}
            placeholder={placeholder}
            name={name}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={onFocus}
            disabled={disabled}
          />
        )}
        {isInvalid && isValidate ? (
          <FormErrorMessage>{t(String(inputError))}</FormErrorMessage>
        ) : null}
      </Flex>
    </FormControl>
  );
};

export default FormControlTextArea;
